import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { getTranslation } from '../utils/constants';
import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';

const LanguageContext = createContext();

const APP_LANGUAGE_KEY = 'appLanguage';

function parseStoredLanguage(raw) {
  if (raw == null || raw === '') return null;
  if (raw === 'fr' || raw === 'ar') return raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed === 'fr' || parsed === 'ar') return parsed;
  } catch {
    /* plain string */
  }
  return null;
}

function readSavedLanguage() {
  if (typeof window === 'undefined') return 'fr';
  try {
    const engine = getStorageEngine();
    const fromEngine = parseStoredLanguage(engine.get(KV_COLLECTION, APP_LANGUAGE_KEY));
    if (fromEngine) return fromEngine;
    const fromLs = parseStoredLanguage(window.localStorage?.getItem(APP_LANGUAGE_KEY));
    if (fromLs) return fromLs;
  } catch {
    /* ignore */
  }
  return 'fr';
}

const DIR_FLIP_MS = 110;

function applyDocumentLanguage(language, { soft = false } = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const nextDir = language === 'ar' ? 'rtl' : 'ltr';
  const nextLang = language === 'ar' ? 'ar-MR' : 'fr-MR';

  const commit = () => {
    root.dir = nextDir;
    root.lang = nextLang;
  };

  if (!soft || root.dir === nextDir) {
    root.classList.remove('aman-dir-flipping');
    commit();
    return;
  }

  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    commit();
    return;
  }

  root.classList.add('aman-dir-flipping');
  window.setTimeout(() => {
    commit();
    window.requestAnimationFrame(() => {
      root.classList.remove('aman-dir-flipping');
    });
  }, DIR_FLIP_MS);
}

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = readSavedLanguage();
    applyDocumentLanguage(saved);
    return saved;
  });
  const skipNextPersistRef = useRef(true);

  // Re-sync after mount (storage engine may not have been ready during SSR/hydration)
  useEffect(() => {
    const saved = readSavedLanguage();
    if (saved !== currentLanguage) {
      skipNextPersistRef.current = true;
      setCurrentLanguage(saved);
      applyDocumentLanguage(saved);
    }
  }, []);

  useEffect(() => {
    // Initial / re-sync: hard apply. User switches go through switchLanguage (soft).
    if (skipNextPersistRef.current) {
      applyDocumentLanguage(currentLanguage, { soft: false });
      skipNextPersistRef.current = false;
      return;
    }

    try {
      const engine = getStorageEngine();
      engine.put(KV_COLLECTION, APP_LANGUAGE_KEY, currentLanguage);
      window.localStorage?.setItem(APP_LANGUAGE_KEY, currentLanguage);
    } catch {
      /* ignore */
    }
  }, [currentLanguage]);

  const switchLanguage = useCallback((language) => {
    if (language === 'fr' || language === 'ar') {
      applyDocumentLanguage(language, { soft: true });
      setCurrentLanguage(language);
    }
  }, []);

  const t = useCallback((key) => getTranslation(key, currentLanguage), [currentLanguage]);

  const value = useMemo(
    () => ({
      currentLanguage,
      switchLanguage,
      t,
      isRTL: currentLanguage === 'ar'
    }),
    [currentLanguage, switchLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
